import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation, getReturnUrl } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import signInAsGuest from "@insite/client-framework/Store/Pages/SignIn/Handlers/SignInAsGuest";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import CreateUserAccoutModal from "@TKCCP/Components/CreateUserAccoutModal";
import LoadCustomerDetailModal from "@TKCCP/Components/LoadCustomerDetailModal";
import { themeColor } from "@TKCCP/Styles/variables";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    text = "text",
    fullWidthButton = "fullWidthButton",
}

const mapStateToProps = (state: ApplicationState) => {
    let { search } = getLocation(state);
    const { allowCreateAccount, allowGuestCheckout } = getSettingsCollection(state).accountSettings;
    let returnUrl = getReturnUrl(state);
    if (!returnUrl && state.components.signInOverlay.isOpen) {
        returnUrl = getLocation(state).pathname;
        if (!search) {
            search = `?returnUrl=${returnUrl}`;
        }
    }

    const shippingPageUrl = getPageLinkByPageType(state, "CheckoutShippingPage")?.url;
    const referredFromShipping = returnUrl === shippingPageUrl;
    const onePageCheckoutPageUrl = getPageLinkByPageType(state, "OnePageCheckoutPage")?.url;
    const referredFromOnePageCheckout = returnUrl === onePageCheckoutPageUrl;
    const createAccountPageLink = getPageLinkByPageType(state, "CreateAccountPage");
    const customerRegistrationLink = getPageLinkByPageType(state, "CustomerRegistrationPage");
    return {
        allowCreateAccount,
        allowGuestCheckout:
            allowGuestCheckout &&
            !getCurrentUserIsGuest(state) &&
            (referredFromShipping || referredFromOnePageCheckout),
        returnUrl,
        createAccountUrl: createAccountPageLink ? `${createAccountPageLink.url}${search}` : undefined,
        customerRegistrationUrl: customerRegistrationLink ? `${customerRegistrationLink.url}${search}` : undefined,
        settings: getSettingsCollection(state),
        isGuest: state.context.session?.isGuest,
        isAuthenticated: state.context.session?.isAuthenticated,
    };
};

const mapDispatchToProps = {
    signInAsGuest,
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.text]: string;
        [fields.fullWidthButton]: boolean;
    };
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface SignInCreateNewAccountStyles {
    signInCreateNewAccountGridContainer?: GridContainerProps;
    signInCreateNewAccountTitleGridItem?: GridItemProps;
    signInGuestCheckoutTitleGridItem?: GridItemProps;
    signInCreateNewAccountTextGridItem?: GridItemProps;
    signInCreateNewAccountButtonGridItem?: GridItemProps;
    signInCreateNewAccountTitle?: TypographyProps;
    signInCreateNewAccountText?: TypographyProps;
    checkoutAsGuestButton?: ButtonPresentationProps;
    createNewAccountButton?: ButtonPresentationProps;
    orTextGridItem?: GridItemProps;
    createButtonText?: TypographyProps;
}

export const signInCreateNewAccountStyles: SignInCreateNewAccountStyles = {
    signInCreateNewAccountGridContainer: {
        gap: 20,
    },
    signInCreateNewAccountTitle: {
        variant: "h4",
        css: css`
            margin-bottom: 0;
            text-transform: uppercase;
        `,
    },
    signInCreateNewAccountTitleGridItem: {
        width: 12,
        css: css`
            padding-bottom: 0px;
        `,
    },
    signInGuestCheckoutTitleGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    signInCreateNewAccountTextGridItem: {
        width: 12,
        css: css`
            padding-top: 0px;
        `,
    },
    signInCreateNewAccountButtonGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-direction: column;
                        `,
                        null,
                        css`
                            flex-direction: column;
                        `,
                        css`
                            flex-direction: column;
                        `,
                        css`
                            flex-direction: column;
                            justify-content: space-between;
                        `,
                    ],
                    "min",
                )}
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            button {
                                width: 100%;
                            }
                        `,
                    ],
                    "min",
                )}
        `,
    },
    checkoutAsGuestButton: {
        variant: "tertiary",
        css: css`
            margin-top: 20px;
            width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-bottom: 1rem;
                        `,
                        null,
                        css`
                            margin-right: 1rem;
                        `,
                        css`
                            margin-bottom: 1rem;
                        `,
                        css`
                            margin-right: 1rem;
                        `,
                    ],
                    "min",
                )}
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    createNewAccountButton: {
        variant: "primary",
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    orTextGridItem: {
        width: [12, 12, 7, 12, 12],
        css: css`
            align-items: center;
            .orText {
                padding: 0 5px;
            }
            .borderLine {
                border: 1px dashed ${themeColor.colorGrey};
                width: calc(50% - 18px);
            }
        `,
    },
    createButtonText: {
        css: css`
            color: ${themeColor.colorSecondary};
            width: 100%;
            text-align: center;
            font-weight: 700;
        `,
    },
};

/**
 * @deprecated Use signInCreateNewAccountStyles instead.
 */
export const signInCreateNewAccount = signInCreateNewAccountStyles;
const styles = signInCreateNewAccountStyles;

const SignInCreateNewAccount: FC<Props> = ({
    history,
    fields,
    allowCreateAccount,
    allowGuestCheckout,
    returnUrl,
    createAccountUrl,
    customerRegistrationUrl,
    signInAsGuest,
    settings,
    isGuest,
    isAuthenticated,
}) => {
    if (!allowCreateAccount) {
        return null;
    }

    const handleCheckoutAsGuest = () => {
        if (!returnUrl) {
            return;
        }

        signInAsGuest({ returnUrl });
    };

    const handleCreateNewAccount = () => {
        if (!createAccountUrl) {
            return;
        }
        // {CreateUserAccoutModal}

        history.push(createAccountUrl);
    };
    const handleCustomerRegistration = () => {
        if (!customerRegistrationUrl) {
            return;
        }
        history.push(customerRegistrationUrl);
    };

    const createButtonCss = fields.fullWidthButton
        ? css`
              width: 100%;
          `
        : undefined;

    const enableCustRegistration = settings?.customerRegistrationSetting;

    return (
        <GridContainer {...styles.signInCreateNewAccountGridContainer}>
            {allowGuestCheckout ? (
                <GridItem {...styles.signInGuestCheckoutTitleGridItem}>
                    <Typography {...styles.signInCreateNewAccountTitle}>{translate("Checkout as a guest")}</Typography>
                    <Typography {...styles.signInCreateNewAccountText}>
                        {siteMessage("checkoutGuest_subtitle")}
                    </Typography>
                    <Button
                        {...styles.checkoutAsGuestButton}
                        onClick={handleCheckoutAsGuest}
                        data-test-selector="signInCreateNewAccount_checkoutAsGuest"
                    >
                        {translate("Checkout as Guest")}
                    </Button>
                </GridItem>
            ) : null}

            <GridItem {...styles.signInCreateNewAccountTitleGridItem}>
                <Typography {...styles.signInCreateNewAccountTitle}>
                    {siteMessage("signin_CreateAccountTitle_account")}
                </Typography>
            </GridItem>
            <GridItem {...styles.signInCreateNewAccountTextGridItem}>
                <Typography {...styles.signInCreateNewAccountText}>{fields.text}</Typography>
            </GridItem>
            <GridItem {...styles.signInCreateNewAccountButtonGridItem}>
                {createAccountUrl ? (
                    <CreateUserAccoutModal history={history} allowGuestCheckout={allowGuestCheckout} />
                ) : // <Button
                //     {...styles.createNewAccountButton}
                //    css={createButtonCss}
                //     onClick={handleCreateNewAccount}
                //     data-test-selector="signInCreateNewAccount_createNewAccount"
                // >

                //     {translate("Create Account")}
                // </Button>
                null}
            </GridItem>
            {/* <GridItem {...styles.signInCreateNewAccountButtonGridItem}>

            <LoadCustomerDetailModal history={history} />

            </GridItem> */}

            {enableCustRegistration ? (
                <GridItem {...styles.signInCreateNewAccountButtonGridItem}>
                    {/* <Button
                    {...styles.createNewAccountButton}
                    onClick={handleCustomerRegistration}
                    data-test-selector="signInCreateNewAccount_customerRegistration"
                >
                    {translate("Register New Account with us")}
                </Button> */}
                    <LoadCustomerDetailModal history={history} allowGuestCheckout={allowGuestCheckout} />
                </GridItem>
            ) : null}
            {/* <Typography {...styles.createButtonText}>
                {siteMessage("signin_customerAccount_text")}
            </Typography> */}

            {/* <GridItem {...styles.signInCreateNewAccountButtonGridItem}>
                <LoadCustomerDetailModal history={history} />
            </GridItem> */}
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(SignInCreateNewAccount)),
    definition: {
        allowedContexts: ["SignInPage", "SignInOverlay"],
        group: "Sign In",
        icon: "User",
        fieldDefinitions: [
            {
                name: fields.text,
                displayName: "Main Text",
                editorTemplate: "MultilineTextField",
                defaultValue: "Become a customer to checkout faster, view order history, save product lists, and more!",
                fieldType: "Translatable",
                sortOrder: 0,
            },
            {
                name: fields.fullWidthButton,
                displayName: "Full Width Button",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
    },
};

export default widgetModule;
